<template>
  <v-container
    fluid
    class="pa-6">
    <!-- Page Title -->
    <div class="text-h5 font-weight-bold mb-1">
      ตั้งค่าวันหยุดและเวลาปิดทำการ
    </div>
    <div class="body-2 grey--text mb-5">
      กำหนดวันหยุดนักขัตฤกษ์หรือช่วงเวลาที่คลังสินค้าไม่พร้อมให้บริการ
    </div>

    <v-row>
      <!-- LEFT -->
      <v-col
        cols="12"
        md="7">
        <!-- Calendar Card -->
        <v-card
          rounded="lg"
          elevation="1"
          class="pa-4 mb-4">
          <!-- Month Nav -->
          <div class="d-flex align-center mb-3">
            <span class="subtitle-1 font-weight-bold">{{ monthLabel }}</span>
            <v-spacer />
            <v-btn
              icon
              small
              @click="prevMonth">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              @click="nextMonth">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              small
              depressed
              rounded
              color="secondary"
              outlined
              class="ml-2"
              style="font-size:12px;"
              @click="goToday">
              วันนี้
            </v-btn>
          </div>

          <!-- Day headers -->
          <v-row
            no-gutters
            class="mb-1">
            <v-col
              v-for="d in dayNames"
              :key="d"
              class="text-center">
              <span class="caption grey--text">{{ d }}</span>
            </v-col>
          </v-row>

          <!-- Calendar Grid -->
          <v-row
            v-for="(week, wi) in calendarWeeks"
            :key="wi"
            no-gutters>
            <v-col
              v-for="(day, di) in week"
              :key="di"
              class="pa-1">
              <v-card
                v-if="day"
                flat
                :outlined="selectedDay === day.date || isHoliday(day.date) === null"
                :color="isPastDay(day.date) ? 'grey lighten-3' : cardColor(day)"
                :class="['pa-1', 'd-flex', 'flex-column', 'align-center', { 'elevation-2': selectedDay === day.date }]"
                style="min-height:60px; cursor:pointer; border-radius:10px;"
                :style="selectedDay === day.date ? 'border:2px solid #000000 !important;' : ''"
                :disabled="isPastDay(day.date)"
                @click="selectDay(day.date)">
                <v-avatar
                  :color="isToday(day.date) ? 'secondary' : 'transparent'"
                  size="26">
                  <span
                    :class="isToday(day.date) ? 'white--text' : 'black--text'"
                    style="font-size:12px;">{{ day.num }}</span>
                </v-avatar>
                <v-chip
                  v-if="isHoliday(day.date) === 'full_day'"
                  x-small
                  color="red lighten-4"
                  text-color="red darken-2"
                  class="mt-1 px-1"
                  style="font-size:9px; height:16px;">
                  วันหยุด
                </v-chip>

                <v-chip
                  v-else-if="isHoliday(day.date) === 'specific_time'"
                  x-small
                  color="amber lighten-4"
                  text-color="orange darken-2"
                  class="mt-1 px-1"
                  style="font-size:9px; height:16px;">
                  ระบุเวลา
                </v-chip>
              </v-card>

              <div
                v-else
                style="min-height:60px;" />
            </v-col>
          </v-row>
        </v-card>

        <!-- Holiday List -->
        <v-card
          rounded="lg"
          elevation="1"
          class="pa-4">
          <div class="subtitle-1 font-weight-bold mb-3">
            รายการวันหยุดที่กำหนดไว้
          </div>
          <v-progress-linear
            v-if="loadingHolidays"
            indeterminate
            color="secondary"
            class="mb-2" />
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left caption grey--text">
                    วันที่
                  </th>
                  <th class="text-left caption grey--text">
                    ประเภท
                  </th>
                  <th class="text-left caption grey--text">
                    ช่วงเวลา
                  </th>
                  <th class="text-left caption grey--text">
                    หมายเหตุ
                  </th>
                  <th class="text-left caption grey--text">
                    จัดการ
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="h in holidays"
                  :key="h.id">
                  <td class="body-2">
                    {{ formatDateTH(h.date) }}
                  </td>
                  <td>
                    <v-chip
                      x-small
                      :color="h.type === 'full_day' ? 'red lighten-4' : 'amber lighten-4'"
                      :text-color="h.type === 'full_day' ? 'red darken-2' : 'orange darken-2'">
                      {{ h.type === 'full_day' ? 'วันหยุดเต็มวัน' : 'ระบุเวลา' }}
                    </v-chip>
                  </td>
                  <td class="body-2">
                    <div v-if="h.type === 'full_day'">
                      00:00 - 23:59
                    </div>
                    <div v-else-if="!h.blockedSlots || h.blockedSlots.length === 0">
                      -
                    </div>
                    <div v-else>
                      <div
                        v-for="(slot, i) in sortTime(h)"
                        :key="i">
                        {{ slot.startTime }} - {{ slot.endTime }} <span
                          v-if="slot.status === 'deleted'"
                          :class="{ 'red--text': slot.status === 'deleted' }">(ถูกลบ)</span>
                      </div>
                    </div>
                  </td>
                  <td class="body-2 grey--text">
                    {{ h.reason }}
                  </td>
                  <td>
                    <v-btn
                      :disabled="isPastDay(h.date)"
                      icon
                      x-small
                      color="secondary"
                      :loading="deletingId === h.id"
                      @click="confirmRemove(h)">
                      <v-icon>
                        mdi-delete
                      </v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr v-if="!loadingHolidays && holidays.length === 0">
                  <td
                    colspan="5"
                    class="text-center caption grey--text py-4">
                    ไม่มีรายการวันหยุด
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>

      <!-- RIGHT: Settings Panel -->
      <v-col
        cols="12"
        md="5">
        <v-card
          rounded="lg"
          elevation="1"
          style="overflow:hidden;">
          <v-toolbar
            color="secondary"
            dark
            flat
            dense>
            <v-icon
              small
              class="mr-2">
              mdi-calendar-edit
            </v-icon>
            <span class="subtitle-2">ตั้งค่าสำหรับวันที่ {{ selectedLabel }}</span>
          </v-toolbar>

          <div class="pa-4">
            <div class="caption grey--text mb-2">
              ประเภทวันหยุด
            </div>

            <!-- Full Day Option -->
            <v-card
              outlined
              flat
              :color="form.type === 'full_day' ? 'grey lighten-4' : ''"
              :style="form.type === 'full_day' ? 'border-color:#000000 !important;' : ''"
              class="pa-3 mb-3 d-flex align-center"
              style="cursor:pointer; border-radius:10px;"
              @click="form.type = 'full_day'">
              <v-radio-group
                v-model="form.type"
                hide-details
                class="ma-0 pa-0"
                style="pointer-events:none;">
                <v-radio
                  value="full_day"
                  color="secondary"
                  hide-details
                  class="ma-0" />
              </v-radio-group>
              <div class="ml-2">
                <div class="body-2 font-weight-medium">
                  ทั้งวัน (Full Day)
                </div>
                <div class="caption grey--text">
                  ปิดทำการตลอด 24 ชั่วโมง
                </div>
              </div>
            </v-card>

            <!-- Specific Time Option -->
            <v-card
              outlined
              flat
              :color="form.type === 'specific_time' ? 'grey lighten-4' : ''"
              :style="form.type === 'specific_time' ? 'border-color:#000000 !important;' : ''"
              class="pa-3 mb-4 d-flex align-center"
              style="cursor:pointer; border-radius:10px;"
              @click="form.type = 'specific_time'">
              <v-radio-group
                v-model="form.type"
                hide-details
                class="ma-0 pa-0"
                style="pointer-events:none;">
                <v-radio
                  value="specific_time"
                  color="secondary"
                  hide-details
                  class="ma-0" />
              </v-radio-group>
              <div class="ml-2">
                <div class="body-2 font-weight-medium">
                  ระบุช่วงเวลา (Custom)
                </div>
                <div class="caption grey--text">
                  ปิดเฉพาะบาง Slot ที่กำหนด
                </div>
              </div>
            </v-card>

            <!-- Slot Grid (specific_time only) -->
            <div
              v-if="form.type === 'specific_time'"
              class="mb-4">
              <div class="caption grey--text mb-2">
                เลือก Slot ที่ต้องการปิด
                <span
                  v-if="form.selectedSlots.length"
                  class="secondary--text ml-1">(เลือกแล้ว {{ form.selectedSlots.length }} slot)</span>
              </div>
              <v-progress-linear
                v-if="loadingSlots"
                indeterminate
                color="secondary"
                class="mb-2" />
              <v-row
                v-else
                dense>
                <v-col
                  v-for="slot in activeSlots"
                  :key="slot.id"
                  cols="4">
                  <v-card
                    flat
                    outlined
                    :color="form.selectedSlots.includes(slot.id) ? 'red lighten-5' : 'white'"
                    :style="form.selectedSlots.includes(slot.id)
                      ? 'border-color: #e53935 !important;'
                      : ''"
                    class="pa-2 text-center"
                    style="cursor:pointer; border-radius:8px;"
                    @click="toggleSlot(slot.id)">
                    <div
                      :class="form.selectedSlots.includes(slot.id) ? 'red--text darken-1 font-weight-bold' : 'body-2 font-weight-bold'">
                      {{ slot.startTime }} - {{ slot.endTime }}
                    </div>
                  </v-card>
                </v-col>
                <v-col
                  v-if="!loadingSlots && activeSlots.length === 0"
                  cols="12">
                  <div class="caption grey--text text-center py-2">
                    ไม่มี Slot ที่ใช้งานได้
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Note -->
            <div class="caption grey--text mb-1">
              หมายเหตุ / เหตุผล
            </div>
            <v-textarea
              v-model="form.note"
              outlined
              dense
              hide-details
              rows="3"
              placeholder="ระบุเหตุผล เช่น วันหยุดชดเชยวันฉัตรมงคล..."
              class="mb-5" />

            <!-- Actions -->
            <v-btn
              block
              color="secondary"
              large
              class="mb-3"
              rounded
              elevation="0"
              :loading="saving"
              :disabled="saving"
              @click="save">
              <v-icon
                left
                small>
                mdi-content-save-outline
              </v-icon>
              บันทึกการตั้งค่า
            </v-btn>
            <v-btn
              block
              large
              rounded
              outlined
              elevation="0"
              :disabled="saving"
              @click="resetForm">
              ยกเลิก
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import DeliveryBookingProvider from '@/resources/DeliveryBookingProvider'
import { sortByTime, sortByDate } from '@/assets/js/DateTimeUtils'
import { mapActions } from 'vuex'

export default {
  name: 'WarehouseHoliday',

  data () {
    const today = this.$dayjs()
    return {
      deliveryBookingService: new DeliveryBookingProvider(),

      today,
      current: today.startOf('month'),
      selectedDay: today.format('YYYY-MM-DD'),
      dayNames: ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'],

      // API data
      holidays: [],
      slots: [],

      // Loading states
      loadingHolidays: false,
      loadingSlots: false,
      saving: false,
      deletingId: null,

      // Form
      form: {
        type: 'full_day',
        selectedSlots: [],
        note: ''
      }
    }
  },

  computed: {
    monthLabel () {
      const th = [
        'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
      ]
      return `${th[this.current.month()]} ${this.current.year() + 543}`
    },

    selectedLabel () {
      if (!this.selectedDay) return '-'
      return this.formatDateTH(this.selectedDay)
    },

    calendarWeeks () {
      const first = this.current.startOf('month').day()
      const days = this.current.daysInMonth()
      const cells = []
      for (let i = 0; i < first; i++) cells.push(null)
      for (let d = 1; d <= days; d++) {
        const day = this.current.date(d)
        cells.push({ num: d, date: day.format('YYYY-MM-DD') })
      }
      while (cells.length % 7 !== 0) cells.push(null)
      const weeks = []
      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
      return weeks
    },

    // only active slots for selection
    activeSlots () {
      return this.slots.filter((s) => s.status === 'active')
    },

    // find existing holiday for selected day
    existingHoliday () {
      return this.holidays.find((h) => this.$dayjs(h.date).format('YYYY-MM-DD') === this.selectedDay) || null
    }
  },

  watch: {
    current () {
      this.fetchHolidays()
    },
    selectedDay (date) {
      this.prefillForm(date)
    }
  },

  async mounted () {
    await Promise.all([this.fetchSlots(), this.fetchHolidays()])
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),

    // ─── API ─────────────────────────────────────────────────

    async fetchSlots () {
      this.loadingSlots = true
      try {
        const { data } = await this.deliveryBookingService.getSlots()
        this.slots = sortByTime([...data], 'startTime')
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: 'โหลด Slot ไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.loadingSlots = false
      }
    },

    async fetchHolidays () {
      this.loadingHolidays = true
      try {
        const { data } = await this.deliveryBookingService.getHolidaysByMonth(
          this.current.month() + 1,
          this.current.year()
        )
        this.holidays = sortByDate(data, 'date')
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: 'โหลดวันหยุดไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.loadingHolidays = false
      }
    },

    async save () {
      if (!this.selectedDay) return

      if (this.form.type === 'specific_time' && this.form.selectedSlots.length === 0) {
        this.setSnackbar({
          value: true,
          message: 'กรุณาเลือกอย่างน้อย 1 Slot',
          type: 'error'
        })
        return
      }

      this.saving = true
      try {
        const payload = {
          date: this.selectedDay,
          type: this.form.type,
          blockedSlots: this.form.type === 'specific_time' ? this.form.selectedSlots : [],
          reason: this.form.note || undefined
        }

        if (this.existingHoliday) {
          await this.deliveryBookingService.updateHoliday(this.existingHoliday.id, payload)
        } else {
          await this.deliveryBookingService.createHoliday(payload)
        }

        await this.fetchHolidays()
        this.setSnackbar({
          value: true,
          message: 'บันทึกสำเร็จ',
          type: 'success'
        })
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: e.message || 'บันทึกไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    confirmRemove (holiday) {
      this.setModal({
        value: true,
        title: 'ยืนยันการลบ',
        message: 'ต้องการลบวันหยุดใช่หรือไม่?',
        confirmText: 'ลบ',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'ยกเลิก',
        onConfirm: () => this.removeHoliday(holiday)
      })
    },

    async removeHoliday (holiday) {
      this.deletingId = holiday.id
      try {
        await this.deliveryBookingService.deleteHoliday(holiday.id)
        await this.fetchHolidays()
        if (this.$dayjs(holiday.date).format('YYYY-MM-DD') === this.selectedDay) {
          this.resetForm()
        }
      } catch (e) {
        this.setSnackbar({
          value: true,
          message: 'ลบไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.deletingId = null
      }
    },

    // ─── Calendar ─────────────────────────────────────────────

    prevMonth () {
      this.current = this.current.subtract(1, 'month').startOf('month')
    },
    nextMonth () {
      this.current = this.current.add(1, 'month').startOf('month')
    },
    goToday () {
      this.current = this.today.startOf('month')
      this.selectedDay = this.today.format('YYYY-MM-DD')
    },
    selectDay (date) {
      this.selectedDay = date
    },
    isToday (date) {
      return date === this.today.format('YYYY-MM-DD')
    },
    isHoliday (date) {
      const h = this.holidays.find((x) => this.$dayjs(x.date).format('YYYY-MM-DD') === date)
      return h ? h.type : null
    },
    cardColor (day) {
      const t = this.isHoliday(day.date)
      if (t === 'full_day') return 'red lighten-5'
      if (t === 'specific_time') return 'amber lighten-5'
      return 'white'
    },

    // ─── Form ─────────────────────────────────────────────────

    prefillForm (date) {
      const h = this.holidays.find((x) => this.$dayjs(x.date).format('YYYY-MM-DD') === date)
      if (h) {
        this.form.type = h.type
        this.form.note = h.reason || ''
        this.form.selectedSlots = h.blockedSlots ? h.blockedSlots.map((s) => s.id || s) : []
      } else {
        this.resetForm()
      }
    },

    resetForm () {
      this.form = { type: 'full_day', selectedSlots: [], note: '' }
    },

    toggleSlot (slotId) {
      const idx = this.form.selectedSlots.indexOf(slotId)
      if (idx >= 0) {
        this.form.selectedSlots.splice(idx, 1)
      } else {
        this.form.selectedSlots.push(slotId)
      }
    },

    // ─── Helpers ──────────────────────────────────────────────

    formatDateTH (date) {
      const th = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
      const d = this.$dayjs(date)
      return `${d.date()} ${th[d.month() + 1]} ${d.year() + 543}`
    },
    isPastDay (date) {
      return this.$dayjs(date).isBefore(this.$dayjs(), 'day')
    },
    sortTime (holiday) {
      return sortByTime([...holiday.blockedSlots], 'startTime')
    }
  }
}
</script>
